import React, { useEffect, useState, useCallback } from "react";
import { Bell, Clock, Save, Send, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const DAYS = [
  { v: 0, l: "Lun" }, { v: 1, l: "Mar" }, { v: 2, l: "Mer" }, { v: 3, l: "Gio" },
  { v: 4, l: "Ven" }, { v: 5, l: "Sab" }, { v: 6, l: "Dom" },
];

function urlB64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export const FlyerReviewSettings = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [push, setPush] = useState({ devices: [], log: [], count: 0 });
  const [busy, setBusy] = useState("");
  const [pushState, setPushState] = useState("");

  const loadSettings = useCallback(async () => {
    try { const { data } = await api.get("/admin/flyer/settings"); setS(data.settings); }
    catch { /* noop */ }
  }, []);
  const loadDevices = useCallback(async () => {
    try { const { data } = await api.get("/admin/push/devices"); setPush(data); }
    catch { /* noop */ }
  }, []);

  useEffect(() => { loadSettings(); loadDevices(); }, [loadSettings, loadDevices]);

  const set = (k, v) => setS((o) => ({ ...o, [k]: v }));
  const toggleDay = (d) => setS((o) => {
    const days = new Set(o.service_days || []);
    days.has(d) ? days.delete(d) : days.add(d);
    return { ...o, service_days: [...days].sort() };
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        service_days: s.service_days, open_time: s.open_time, close_time: s.close_time,
        timezone: s.timezone, sla_hours: Number(s.sla_hours), email_enabled: s.email_enabled,
        push_enabled: s.push_enabled, notify_sender: s.notify_sender, notify_recipient: s.notify_recipient,
        sla_reminder_minutes: Number(s.sla_reminder_minutes),
      };
      const { data } = await api.put("/admin/flyer/settings", payload);
      setS(data.settings); toast.success("Impostazioni salvate");
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); }
    finally { setSaving(false); }
  };

  const enablePush = async () => {
    setPushState("");
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window))
        throw new Error("Questo browser non supporta le notifiche push");
      const perm = await Notification.requestPermission();
      if (perm !== "granted") throw new Error("Permesso notifiche negato");
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      const { data } = await api.get("/admin/push/public-key");
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlB64ToUint8Array(data.publicKey),
        });
      }
      const j = sub.toJSON();
      await api.post("/admin/push/subscribe", { endpoint: j.endpoint, keys: j.keys, label: navigator.userAgent });
      setPushState("Notifiche attive su questo dispositivo");
      toast.success("Dispositivo registrato per le notifiche push");
      loadDevices();
    } catch (e) {
      setPushState(e.message || "Attivazione non riuscita");
      toast.error(e.message || "Attivazione non riuscita");
    }
  };

  const sendTest = async () => {
    setBusy("test");
    try {
      const { data } = await api.post("/admin/push/test");
      toast.success(`Notifica di prova inviata (${data.sent}/${data.devices} dispositivi)`);
      loadDevices();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invio non riuscito"); }
    finally { setBusy(""); }
  };

  const toggleDevice = async (endpoint, active) => {
    try { await api.post(`/admin/push/device/${active ? "disable" : "enable"}?endpoint=${encodeURIComponent(endpoint)}`); loadDevices(); }
    catch { toast.error("Operazione non riuscita"); }
  };
  const removeDevice = async (endpoint) => {
    try { await api.delete(`/admin/push/subscribe?endpoint=${encodeURIComponent(endpoint)}`); loadDevices(); }
    catch { toast.error("Rimozione non riuscita"); }
  };

  if (!s) return null;

  return (
    <div data-testid="flyer-review-settings" className="grid lg:grid-cols-2 gap-4 mb-6">
      {/* Orari & notifiche */}
      <div className="rounded-2xl border border-brand-navy/10 bg-white p-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="h-4 w-4 text-brand-electric" />
          <h3 className="font-semibold text-brand-navy text-sm">Orari revisione & SLA</h3>
        </div>
        <div className="space-y-3 text-sm">
          <div>
            <p className="text-xs text-brand-navy/50 mb-1">Giorni di servizio</p>
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map((d) => (
                <button key={d.v} data-testid={`ops-day-${d.v}`} onClick={() => toggleDay(d.v)}
                  className={`text-xs px-2.5 py-1 rounded-full border ${(s.service_days || []).includes(d.v) ? "bg-brand-electric text-white border-brand-electric" : "bg-white text-brand-navy/60 border-brand-navy/15"}`}>
                  {d.l}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-xs text-brand-navy/50">Apertura</span>
              <Input data-testid="ops-open-time" type="time" value={s.open_time} onChange={(e) => set("open_time", e.target.value)} className="rounded-lg" /></label>
            <label className="block"><span className="text-xs text-brand-navy/50">Chiusura</span>
              <Input data-testid="ops-close-time" type="time" value={s.close_time} onChange={(e) => set("close_time", e.target.value)} className="rounded-lg" /></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-xs text-brand-navy/50">Fuso orario</span>
              <Input data-testid="ops-timezone" value={s.timezone} onChange={(e) => set("timezone", e.target.value)} className="rounded-lg" /></label>
            <label className="block"><span className="text-xs text-brand-navy/50">Ore lavorative (SLA)</span>
              <Input data-testid="ops-sla-hours" type="number" step="0.5" min="0" value={s.sla_hours} onChange={(e) => set("sla_hours", e.target.value)} className="rounded-lg" /></label>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <label className="block"><span className="text-xs text-brand-navy/50">Promemoria push scadenza (minuti prima)</span>
              <Input data-testid="ops-reminder-min" type="number" step="5" min="0" value={s.sla_reminder_minutes ?? 30} onChange={(e) => set("sla_reminder_minutes", e.target.value)} className="rounded-lg" /></label>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <label className="block"><span className="text-xs text-brand-navy/50">Mittente notifiche</span>
              <Input data-testid="ops-sender" value={s.notify_sender || ""} onChange={(e) => set("notify_sender", e.target.value)} className="rounded-lg" /></label>
            <label className="block"><span className="text-xs text-brand-navy/50">Destinatario notifiche (riservato)</span>
              <Input data-testid="ops-recipient" value={s.notify_recipient || ""} onChange={(e) => set("notify_recipient", e.target.value)} className="rounded-lg" /></label>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-xs"><input data-testid="ops-email-enabled" type="checkbox" checked={!!s.email_enabled} onChange={(e) => set("email_enabled", e.target.checked)} /> Email attive</label>
            <label className="flex items-center gap-2 text-xs"><input data-testid="ops-push-enabled" type="checkbox" checked={!!s.push_enabled} onChange={(e) => set("push_enabled", e.target.checked)} /> Push attive</label>
          </div>
          <Button data-testid="ops-save-btn" onClick={save} disabled={saving} className="rounded-full text-xs bg-brand-electric text-white">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Salva impostazioni
          </Button>
        </div>
      </div>

      {/* Notifiche push */}
      <div className="rounded-2xl border border-brand-navy/10 bg-white p-4">
        <div className="flex items-center gap-2 mb-3">
          <Bell className="h-4 w-4 text-brand-orange" />
          <h3 className="font-semibold text-brand-navy text-sm">Notifiche push</h3>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          <Button data-testid="push-enable-btn" onClick={enablePush} className="rounded-full text-xs bg-brand-navy text-white">Attiva notifiche su questo dispositivo</Button>
          <Button data-testid="push-test-btn" onClick={sendTest} disabled={busy === "test"} variant="outline" className="rounded-full text-xs">
            {busy === "test" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />} Invia notifica di prova
          </Button>
        </div>
        {pushState && <p data-testid="push-state" className="text-xs text-brand-navy/60 mb-2">{pushState}</p>}
        <p className="text-xs font-semibold text-brand-navy/70 mb-1">Dispositivi autorizzati ({push.count})</p>
        <div className="space-y-1.5 max-h-40 overflow-auto">
          {(push.devices || []).map((d) => (
            <div key={d.endpoint} data-testid="push-device" className="flex items-center justify-between gap-2 text-[11px] rounded-lg border border-brand-navy/10 p-2">
              <span className="truncate flex-1" title={d.label}>{(d.label || "Dispositivo").slice(0, 42)} {d.active ? "" : "· disattivo"}</span>
              <button onClick={() => toggleDevice(d.endpoint, d.active)} className="text-brand-navy/60 hover:underline">{d.active ? "Disattiva" : "Attiva"}</button>
              <button onClick={() => removeDevice(d.endpoint)} className="text-red-500 hover:underline">Rimuovi</button>
            </div>
          ))}
          {!push.devices?.length && <p className="text-[11px] text-brand-navy/40">Nessun dispositivo registrato.</p>}
        </div>
        {!!push.log?.length && (
          <>
            <p className="text-xs font-semibold text-brand-navy/70 mt-3 mb-1">Registro invii</p>
            <div className="space-y-1 max-h-32 overflow-auto text-[11px] text-brand-navy/60">
              {push.log.map((l, i) => (
                <div key={i} data-testid="push-log-item" className="flex justify-between gap-2">
                  <span className="truncate">{l.event} · {l.title}</span>
                  <span>{l.sent}/{l.devices} · {new Date(l.created_at).toLocaleString("it-IT")}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FlyerReviewSettings;
